import { Box, Button, TextField, Typography } from "@mui/material";
import { Container } from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useState } from "react";
import { DocumentPage } from "./DocumentPage";
import DownloadIcon from "@mui/icons-material/Download";
import html2pdf from "html2pdf.js";
import { documentBuilderService } from "../../services/documentBuilderService";
import { Page } from "../../services/documentBuilderService";
import { Snackbar, Alert } from "@mui/material";
import "./DocumentEditor/Editor.css";
import { AddPageSlot } from "./AddPageSlot";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { useTheme } from "@mui/material";
import { Tooltip } from "@mui/material";
import { useRef } from "react";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import FileManagerModal from "./FileManagerModal";

import {
  ArrowBack as ArrowBackIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SettingsIcon from "@mui/icons-material/Settings";
import { Popover, Tabs, Tab } from "@mui/material";
import { documentThemes } from "./DocumentTheme";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachFileIcon from "@mui/icons-material/AttachFile";
export interface AttachmentFile extends File {
  uploadedId?: string;
}

const DocumentBuilder: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { mode, toggleMode } = useThemeMode();

  const [prompt, setPrompt] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [generated, setGenerated] = useState(false);
  const [documentTheme, setDocumentTheme] = useState("default");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<Page[][]>([]);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [uploading, setUploading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [settingsTab, setSettingsTab] = useState(0);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeDocIndex, setActiveDocIndex] = useState<number | null>(null);
  const [fileMenuAnchor, setFileMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [currentFolderContent, setCurrentFolderContent] = useState<any[]>([]);
  const [currentFolderName, setCurrentFolderName] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const openFileMenu = Boolean(fileMenuAnchor);

  const handleOpenFileMenu = (event: React.MouseEvent<HTMLElement>) => {
    setFileMenuAnchor(event.currentTarget);
  };
  const handleFolderClick = async (folderPath: string = "/") => {
    try {
      setFolderModalOpen(true);
      const data = await documentBuilderService.listDocuments(folderPath);

      setCurrentFolderName(folderPath);
      setCurrentFolderContent(data); // API response
    } catch (err) {
      console.error(err);
      alert("Failed to load documents");
    }
  };
  const ALLOWED_EXTENSIONS = [
    "jpeg",
    "jpg",
    "png",
    "doc",
    "docx",
    "ppt",
    "txt",
    "xls",
    "xlsx",
    "csv",
  ];
  const isValidFile = (file: File) => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    return extension && ALLOWED_EXTENSIONS.includes(extension);
  };
  const handleFileUpload = async (files: FileList | null) => {
    if (!files) return;

    const newFiles = Array.from(files);

    let duplicateFound = false;
    let ignoredFiles = 0;
    let invalidFiles = 0;

    //  Step 1: filter valid + unique + within limit
    const filteredFiles: File[] = [];

    for (const file of newFiles) {
      // check type
      if (!isValidFile(file)) {
        invalidFiles++;
        continue;
      }

      // check duplicate (compare with existing attachments)
      const isDuplicate = attachments.some(
        (f) => f.name === file.name && f.size === file.size,
      );

      if (isDuplicate) {
        duplicateFound = true;
        continue;
      }

      // check limit (existing + new)
      if (attachments.length + filteredFiles.length < 10) {
        filteredFiles.push(file);
      } else {
        ignoredFiles++;
      }
    }

    //  Alerts
    if (invalidFiles > 0) {
      setSnackbarOpen(true);
    }

    if (duplicateFound) {
      alert("You cannot upload the same file twice.");
    }

    if (ignoredFiles > 0) {
      alert("You can upload a maximum of 10 files.");
    }

    if (filteredFiles.length === 0) return;

    setUploading(true);

    try {
      const response = await documentBuilderService.uploadDocuments(
        filteredFiles,
        "/chat",
      );

      const uploadedDocs = response?.documents || [];

      const filesWithIds: AttachmentFile[] = filteredFiles.map(
        (file, index) => {
          const uploadedId = uploadedDocs[index]?.id;

          return Object.assign(file, { uploadedId });
        },
      );

      const newIds = uploadedDocs.map((doc: any) => doc.id);

      setUploadedFileIds((prev) => [...prev, ...newIds]);
      setAttachments((prev) => [...prev, ...filesWithIds]);
    } catch (err) {
      console.error(err);
      alert("File upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleS3FileSelect = async (file: any) => {
    try {
      const fileType = file.mime_type || file.type;

      const convertedFile = new File([], file.title, {
        type: fileType,
      }) as File & { preview?: string; uploadedId?: string };

      convertedFile.preview = file.s3_url;
      convertedFile.uploadedId = file.id; //  attach id to file

      setAttachments((prev) => {
        const combined = [...prev];
        let duplicateFound = false;
        let ignoredFiles = 0;

        const isDuplicate = combined.some(
          (f) => f.name === convertedFile.name && f.size === convertedFile.size,
        );

        if (isDuplicate) {
          duplicateFound = true;
        } else if (combined.length < 10) {
          combined.push(convertedFile);

          // ADD THIS: store selected S3 file ID
          if (file.id) {
            setUploadedFileIds((prevIds) => {
              if (prevIds.includes(file.id)) return prevIds; // prevent duplicates
              return [...prevIds, file.id];
            });
          }
        } else {
          ignoredFiles++;
        }

        if (duplicateFound) {
          alert("You cannot upload the same file twice.");
        }

        if (ignoredFiles > 0) {
          alert("You can upload a maximum of 10 files at a time.");
        }

        return combined;
      });
    } catch (err) {
      console.error("Failed to load file from S3", err);
    }
  };
  const handleCloseFolderModal = () => {
    setFolderModalOpen(false);
    setCurrentFolderContent([]);
    setCurrentFolderName("");
  };
  console.log(uploadedFileIds, "ggg");
  const handleCloseFileMenu = () => {
    setFileMenuAnchor(null);
  };

  const openSettings = Boolean(settingsAnchor);

  const handleOpenSettings = (
    event: React.MouseEvent<HTMLElement>,
    docIndex: number,
  ) => {
    setSettingsAnchor(event.currentTarget);
    setActiveDocIndex(docIndex);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;

    if (files && files.length > 0) {
      handleFileUpload(files); //  hit upload API directly
    }
  };

  const handleCloseSettings = () => {
    setSettingsAnchor(null);
    setActiveDocIndex(null);
  };

  const handleRemovePage = (docIndex: number, pageId: string) => {
    setDocuments((prevDocs) =>
      prevDocs.map((doc, dIndex) => {
        if (dIndex !== docIndex) return doc;

        return doc.filter((page) => page.id !== pageId);
      }),
    );
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setLoading(true);

    try {
      //  Send prompt + files
      const response = await documentBuilderService.generateAIDocument({
        prompt,
        fileIds: uploadedFileIds,
      });

      const pagesWithId = response.pages.map((page: any) => ({
        ...page,
        id: crypto.randomUUID(),
      }));

      setDocuments((prev) => [...prev, pagesWithId]);
      setGenerated(true);
      setPrompt("");
      setAttachments([]); // clear files after sending
    } catch (err) {
      console.error(err);
      alert("Failed to generate document. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleAddPage = (docIndex: number, insertIndex: number) => {
    setDocuments((prevDocs) =>
      prevDocs.map((doc, dIndex) => {
        if (dIndex !== docIndex) return doc;

        const newPage: Page = {
          id: crypto.randomUUID(), //  add this
          header: "",
          body: "<p><br/></p>",
        };
        const updated = [...doc];
        updated.splice(insertIndex, 0, newPage);

        return updated;
      }),
    );
  };

  const handleDownload = (docIndex: number) => {
    const element = document.getElementById(`document-print-area-${docIndex}`);
    if (!element) return;

    //  Add print-mode to body
    document.body.classList.add("print-mode");

    const options: any = {
      margin: 10,
      filename: `document-${docIndex + 1}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        scale: 2,
        useCORS: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
      pagebreak: {
        mode: ["avoid-all", "css", "legacy"],
      },
    };

    html2pdf()
      .set(options)
      .from(element)
      .save()
      .then(() => {
        //  Remove print-mode after PDF generated
        document.body.classList.remove("print-mode");
      });
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        position: "relative",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          py: 4,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton onClick={() => navigate("/chat")} size="small">
                <ArrowBackIcon />
              </IconButton>
              <img
                src="/atlasai-icon.svg"
                alt="CoreSupport"
                style={{ width: 32, height: 32 }}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    lineHeight: 1.2,
                  }}
                >
                  Core Support
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}
                >
                  Document Builder
                </Typography>
              </Box>
            </Box>
            <Tooltip
              title={
                mode === "dark" ? "Switch to light mode" : "Switch to dark mode"
              }
            >
              <IconButton onClick={toggleMode} color="primary">
                {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Create, edit, and format multi-page documents with structured
            layouts.
          </Typography>
        </Box>

        <Box px={{ xs: 0, sm: 0, md: 4 }} py={{ xs: 0, sm: 0, md: 6 }} maxWidth="900px" mx="auto">
          {/* ===== Hero Section ===== */}
          <Box textAlign="center" mb={4}>
            <Typography
              sx={{
                fontSize: {
                  xs: "32px", // default mobile
                  sm: "32px",
                  md: "40px",
                },
                "@media (max-width:400px)": {
                  fontSize: "26px",
                },
                fontWeight: 700,
                mb: 1,
                color: "#4F46E5",
              }}
            >
              Document Builder
            </Typography>
          </Box>

          {/* ===== Generated Document Pages ===== */}

          {documents.map((docPages, docIndex) => (
            <Box key={docIndex} mb={6}>
              <IconButton
                onClick={(e) => handleOpenSettings(e, docIndex)}
                sx={{
                  position: "absolute",
                  right: 5, //  beside document
                  top: {
                    xs: "57%", // mobile → more down
                    sm: "57%",
                    md: "57%",
                    lg: "54%", // desktop → original
                  },
                  zIndex: 20,
                  width: "50px",
                  height: "50px",
                  backgroundColor: "#111827",
                  color: "#fff",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  transition: "all 0.3s ease",

                  "&:hover": {
                    backgroundColor: "#4F46E5",
                  },
                }}
              >
                <SettingsIcon />
              </IconButton>
              {/* Download Button For This Document */}
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(docIndex)}
                  sx={{
                    borderRadius: "999px",
                    px: 4,
                    backgroundColor: "#4F46E5",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#4338CA",
                    },
                  }}
                >
                  Download
                </Button>
              </Box>

              {/* Pages */}
              <Box id={`document-print-area-${docIndex}`}>
                <Box className="no-print">
                  <AddPageSlot onClick={() => handleAddPage(docIndex, 0)} />
                </Box>

                {docPages.map((page, pageIndex) => (
                  <Box
                    key={page.id}
                    sx={{
                      position: "relative",
                      "&:hover .delete-btn": {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    }}
                  >
                    <IconButton
                      className="delete-btn"
                      onClick={() => handleRemovePage(docIndex, page.id)}
                      size="small"
                      sx={{
                        position: "absolute",
                        right: 0,
                        top: "50%",
                        transform: "translateY(-50%) translateX(10px)",
                        width: "50px",
                        height: "50px",
                        backgroundColor: "#4F46E5",
                        color: "#fff",
                        opacity: 0,
                        transition: "all 0.3s ease",
                        zIndex: 10,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        "&:hover": {
                          backgroundColor: "#4338CA",
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>

                    <DocumentPage
                      theme={documentTheme}
                      setDocumentTheme={setDocumentTheme}
                      isFirst={pageIndex === 0}
                      content={
                        pageIndex === 0
                          ? `${page.header || ""}${page.body}`
                          : page.body
                      }
                    />

                    {/* Add Slot After Each Page */}
                    <Box className="no-print">
                      <AddPageSlot
                        onClick={() => handleAddPage(docIndex, pageIndex + 1)}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
          {/* ===== Settings Popover ===== */}
          <Popover
            open={openSettings}
            anchorEl={settingsAnchor}
            onClose={handleCloseSettings}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            disableScrollLock={true} // prevents MUI from trying to reposition for scroll
            PaperProps={{
              sx: {
                width: 350,
                p: 1,
                borderRadius: 3,
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              },
            }}
          >
            {/* Tabs */}
            <Tabs
              value={settingsTab}
              onChange={(e, newValue) => setSettingsTab(newValue)}
              sx={{ mb: 1 }}
            >
              <Tab label="Themes" />
              <Tab label="Colors" />
            </Tabs>

            {/* Tab Content */}
            {settingsTab === 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr", // 2 per row
                  gap: 1,
                  maxHeight: "380px",
                }}
              >
                {documentThemes.map((theme) => (
                  <Box
                    key={theme.id}
                    onClick={() => {
                      setDocumentTheme(theme.id);
                    }}
                    sx={{
                      width: 150,
                      cursor: "pointer",
                      borderRadius: 2,
                      p: 1,
                      transition: "all 0.2s ease",

                      border: "4px solid transparent",

                      /*Correct active check */
                      ...(documentTheme === theme.id && {
                        borderColor: "#4F46E5",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      }),

                      "&:hover": {
                        borderColor: "#4F46E5",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    {/* Preview */}
                    <Box
                      sx={{
                        border: "1px solid #E5E7EB",
                        p: 1,
                        backgroundColor: theme.background
                          ? `url(${theme.background})`
                          : "none",

                        backgroundImage: theme.backgroundImage
                          ? `url(${theme.backgroundImage})`
                          : "none",
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: theme.titleFont,
                          fontWeight: 700,
                          fontSize: 14,
                          color: theme.titleColor,
                          position: "relative",
                          mb: 1,

                          ...(theme.headingStyle === "underline" && {
                            borderBottom: `2px solid ${theme.titleColor}`,
                            paddingBottom: "4px",
                          }),

                          ...(theme.headingStyle === "left-accent" && {
                            paddingLeft: "12px",

                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: "0.2em",
                              height: "20px",
                              width: "3px",
                              backgroundColor: theme.titleColor,
                              borderRadius: "2px",
                            },
                          }),

                          ...(theme.headingStyle === "center-line" && {
                            textAlign: "center",

                            "&::after": {
                              content: '""',
                              display: "block",
                              width: "40px",
                              height: "2px",
                              backgroundColor: theme.titleColor,
                              margin: "6px auto 0",
                            },
                          }),
                        }}
                      >
                        TITLE
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily: theme.bodyFont,
                          fontSize: 12,
                          fontWeight: 600,
                          color: theme.bodyColor,
                          mb: 0.5,
                        }}
                      >
                        Heading 1
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: theme.bodyFont,
                          fontSize: 11,
                          color: theme.bodyColor,
                          opacity: 0.8,
                        }}
                      >
                        This is preview text .
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Popover>

          {/* ===== Prompt Box ===== */}
          <Box mb={4} mt={4} sx={{ position: "relative" }}>
            <Box
              sx={{
                position: "relative",
                border: isDragging
                  ? "2px dashed #4F46E5"
                  : "2px dashed transparent",
                borderRadius: 3,
                transition: "all 0.2s ease",
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isDragging && (
                <Box
                  sx={(theme) => ({
                    position: "absolute",
                    inset: 0,
                    zIndex: 5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 3,

                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0,0,0,0.6)"
                        : "rgba(255,255,255,0.7)",

                    backdropFilter: "blur(4px)",

                    color: "#4F46E5",
                    fontSize: 20,
                    fontWeight: 600,
                    pointerEvents: "none", //  IMPORTANT
                  })}
                >
                  Drop files here
                </Box>
              )}
              <Box
                sx={{
                  border: "2px solid #9CA3AF",
                  borderRadius: 3,
                  p: 2,
                  "&:focus-within": {
                    borderColor: "#4F46E5",
                  },
                }}
              >
                {/* 1️⃣ PREVIEWS */}
                {attachments.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {attachments.map((file, index) => {
                      const isImage =
                        file.type.startsWith("image/") ||
                        file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                      const previewUrl =
                        (file as any).preview || URL.createObjectURL(file);

                      return (
                        <Box
                          key={index}
                          sx={{
                            position: "relative",
                            width: 73,
                            height: 70,
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "1px solid #E5E7EB",
                            backgroundColor: "#F9FAFB",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              setAttachments((prev) =>
                                prev.filter((_, i) => i !== index),
                              )
                            }
                            sx={{
                              position: "absolute",
                              top: 2,
                              right: 4,
                              width: 18,
                              height: 18,
                              p: 0,
                              backgroundColor: "#111",
                              color: "#fff",
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 12 }} />
                          </IconButton>

                          {isImage ? (
                            <img
                              src={previewUrl}
                              alt=""
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              height="100%"
                            >
                              <DescriptionIcon fontSize="small" />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* ICONS + TEXTAREA SAME ROW */}
                <Box
                    sx={{
                    display: "flex",
                    flexDirection: "column",   //  CHANGE THIS
                    gap: 1,
                    }}
                  >
                  {/* Icons */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mt: "-6px",
                    }}
                  >
                    <IconButton
                      onClick={handleOpenFileMenu}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#F3F4F6",
                        color: "#4F46E5",
                        "&:hover": {
                          backgroundColor: "#4F46E5",
                          color: "#fff",
                        },
                      }}
                    >
                      <AddIcon />
                    </IconButton>

                    <IconButton
                      onClick={() => handleFolderClick("/")}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#F3F4F6",
                        color: "#4F46E5",
                        "&:hover": {
                          backgroundColor: "#4F46E5",
                          color: "#fff",
                        },
                      }}
                    >
                      <FolderOpenIcon />
                    </IconButton>
                  </Box>

                  {/* Textarea */}
                  <TextField
                    placeholder="Write a subject line for an email introducing a new CEO"
                    multiline
                    minRows={3}
                    fullWidth
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    sx={{
                      "& textarea::placeholder": {
                        color: "#9CA3AF",
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Dropdown Menu */}
            <Menu
              anchorEl={fileMenuAnchor}
              open={openFileMenu}
              onClose={handleCloseFileMenu}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  width: 220,
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  handleCloseFileMenu();
                  fileInputRef.current?.click();
                }}
              >
                <ListItemIcon>
                  <AttachFileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add photos & files</ListItemText>
              </MenuItem>
            </Menu>
            <input
              type="file"
              hidden
              multiple
              ref={fileInputRef}
              accept=".jpeg,.jpg,.png,.doc,.docx,.ppt,.txt,.xls,.xlsx,.csv"
              onChange={(e) => handleFileUpload(e.target.files)}
            />
          </Box>

          {/* ===== Action Buttons ===== */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mb: 6,
              flexDirection: {
                xs: "column",
                sm: "row",
              },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<InfoOutlinedIcon />}
              sx={{
                borderRadius: "999px",
                px: 3,
                borderColor: "#9CA3AF",
                color: "#4F46E5",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              See how it works
            </Button>

            <Button
              variant="contained"
              startIcon={<AutoFixHighIcon />}
              onClick={handleGenerate}
              disabled={loading}
              sx={{
                borderRadius: "999px",
                px: 4,
                backgroundColor: "#0F9D58",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#0C7C45",
                },
              }}
            >
              {loading ? "Generating..." : "Generate Document"}
            </Button>
          </Box>
        </Box>
      </Container>
      <FileManagerModal
        open={folderModalOpen}
        onClose={handleCloseFolderModal}
        currentFolderName={currentFolderName || "/"}
        currentFolderContent={currentFolderContent}
        onFileSelect={handleS3FileSelect}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Only these file types are allowed: jpeg, jpg, png, doc, docx, ppt,
          txt, xls, xlsx, csv
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentBuilder;
